import { useNavigate, useParams } from "react-router-dom";
import useCampaignsService from "../../services/CampaignsService";
import { FormEvent, useEffect, useState } from "react";
import useUrlUtilities from "../../utilities/UrlUtilities";
import { Campaign } from "../../types/Campaigns";
import BackButton from "../../components/BackButton";
import PageTitle from "../../components/PageTitle";
import LoadingEllipsis from "../../components/LoadingEllipsis";
import Ghost from "../../components/Ghost";
import NegativeButton from "../../components/NegativeButton";
import PositiveButton from "../../components/PositiveButton";
import SimpleAlert from "../../components/SimpleAlert";
import useTitlesService from "../../services/TitlesService";
import TitlePicker from "../../components/Title/TitlePicker";
import CampaignRewardsList from "../../components/Campaigns/CampaignRewardsList";
import CampaignPreviewUsersList from "../../components/Campaigns/CampaignPreviewUsersList";

export default function AdminCampaignsDetailPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const { getCampaign, updateCampaign, deleteCampaign, createCampaign, approveCampaign } =
    useCampaignsService();
  const [isLoading, setIsLoading] = useState(true);
  const [initiated, setInitiated] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isApproved, setIsApproved] = useState<Boolean | undefined>(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    name: "",
    subtitle: "",
    startTime: "",
    endTime: "",
    titleId: "",
    campaignImageUrl: "",
    actionType: "",
    destinationUrl: "",
    appStoreId: "",
    buttonText: "",
    image: null,
  });

  const approveThisCampaign = async () => {
    setIsSaving(true);
    try {
      await approveCampaign(formData.id);
      setIsApproved(true);
      setIsApproving(false);
    } catch (error) {
      console.error("Error approving campaign:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteThisCampaign = () => {
    setIsDeleting(true);
    deleteCampaign(formData.id)
      .then(() => {
        navigate("/admin/campaigns");
      })
      .catch((error) => {
        console.error("Error deleting campaign:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title) {
      errors.title = "Title is required";
    }

    if (!formData.name) {
      errors.name = "Name is required";
    }

    // If the form is for a new article, ensure the image file is provided
    if (!formData.id && !formData.image) {
      errors.image = "Image file is required for new campaigns.";
    }

    if (formData.image) {
      var imageFile = formData.image as File;
      if (!imageFile.name.match(/\.(jpg|jpeg)$/)!) {
        errors.image = "Image file must be a JPG or JPEG.";
      }
    }

    // Call-to-action validation. The action type is optional, but if one is
    // chosen the button text and the matching destination field are required.
    if (formData.actionType) {
      if (!formData.buttonText) {
        errors.buttonText =
          "Button text is required when a call to action is set.";
      }

      if (formData.actionType === "web_url" && !formData.destinationUrl) {
        errors.destinationUrl =
          "Destination URL is required for a web URL call to action.";
      }

      if (formData.actionType === "store_page" && !formData.appStoreId) {
        errors.appStoreId =
          "Store ID is required for a store page call to action.";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveCampaign = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", formData.title);
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("subtitle", formData.subtitle);
    formDataToSubmit.append("startTime", formData.startTime);
    formDataToSubmit.append("endTime", formData.endTime);
    formDataToSubmit.append("titleId", formData.titleId);
    formDataToSubmit.append("actionType", formData.actionType || "");
    formDataToSubmit.append(
      "destinationUrl",
      formData.actionType === "web_url" ? formData.destinationUrl || "" : ""
    );
    formDataToSubmit.append(
      "appStoreId",
      formData.actionType === "store_page" ? formData.appStoreId || "" : ""
    );
    formDataToSubmit.append(
      "buttonText",
      formData.actionType ? formData.buttonText || "" : ""
    );

    // Load the file as a blob.
    if (formData.image) {
      const imageBlob = await new Promise<Blob>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const blob = new Blob([reader.result as ArrayBuffer]);
          resolve(blob);
        };
        reader.readAsArrayBuffer(formData.image!);
      });
      formDataToSubmit.append("Image", imageBlob);
    }

    var savedCampaign: Campaign | null = null;

    if (formData.id) {
      var updateResult = await updateCampaign(formData.id, formDataToSubmit);

      savedCampaign = updateResult as Campaign;

      // If the result is a http error
      if (!savedCampaign) {
        alert("Error creating campaign");
        setIsSaving(false);
        return;
      }
    } else {
      var createResult = await createCampaign(formDataToSubmit);
      savedCampaign = createResult as Campaign;

      if (!savedCampaign) {
        alert("Error creating campaign");
        setIsSaving(false);
        return;
      }
    }

    setFormData({
      ...formData,
      id: savedCampaign.id,
      campaignImageUrl: savedCampaign.campaignImageUrl!,
    });

    setIsClean(true);
    setIsSaving(false);

    const cameFromNew = campaignId?.toLowerCase() === "new";

    if (cameFromNew) {
      navigate(`/admin/campaigns/${savedCampaign.id}`, { replace: true });
    }
  };

  const isFormValid = submitted && Object.keys(validationErrors).length === 0;

  const validationError = (fieldName: string): null | string => {
    return validationErrors[fieldName] || null;
  };

  const validStyle = (fieldName: string) => {
    // Find the validationErrors key with the fieldName.
    let valid = Object.keys(validationErrors).indexOf(fieldName) === -1;

    return valid ? "" : "border-red-800";
  };

  const handleImageFileChange = (e: any) => {
    const { files } = e.target;

    setIsClean(false);

    if (!files || files.length === 0) {
      setFormData({
        ...formData,
        image: null,
      });

      setPreviewBlob(null);

      return;
    }

    setFormData({
      ...formData,
      image: files[0],
    });

    // Load the image and set the preview blob.
    const reader = new FileReader();
    reader.onload = () => {
      const blob = new Blob([reader.result as ArrayBuffer]);
      setPreviewBlob(blob);
    };
    reader.readAsArrayBuffer(files[0]);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (initiated) {
      return;
    }

    setIsLoading(true);
    setInitiated(true);

    if (campaignId?.toLowerCase() == "new") {
      setIsLoading(false);
    } else {
      getCampaign(campaignId as string)
        .then((result) => {
          var campaign = result as Campaign;

          setIsApproved(campaign.isApproved);

          // Dates are in the format "2025-10-24T00:00:00".

          // Convert start and end time  for datetime-local input
          var startTime = campaign.startTime.toString();
          var endTime =
            campaign.endTime == null ? "" : campaign.endTime.toString();

          setFormData({
            id: campaign.id,
            title: campaign.title,
            name: campaign.name,
            image: null,
            startTime: startTime,
            endTime: endTime,
            subtitle: campaign.subtitle || "",
            titleId: campaign.titleId || "",
            campaignImageUrl: campaign.campaignImageUrl || "",
            actionType: campaign.actionType || "",
            destinationUrl: campaign.destinationUrl || "",
            appStoreId: campaign.appStoreId || "",
            buttonText: campaign.buttonText || "",
          });
        })
        .catch((error) => {
          console.error("Error fetching campaign:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [initiated]);

  return (
    <section className="flex flex-col gap-2 divide-stone-900">
      <div className="flex flex-row">
        <BackButton />
        <PageTitle className="text-4xl">
          {isLoading
            ? "Loading"
            : formData.id
            ? formData.title
            : "New Campaign"}
          {isLoading && <LoadingEllipsis />}
        </PageTitle>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col gap-2 col-span-2">
          <h2 className="text-2xl">Details</h2>
          {isLoading && <Ghost additionalClasses="h-24 col-span-2 rounded" />}
          {!isLoading && formData && (
            <form onSubmit={saveCampaign} className="form">
              <div className="grid grid-cols-1 gap-x-6 gap-y-8 xl:grid-cols-2">
                <div className="card p-8 flex flex-col gap-4">
                  <div className="">
                    <label htmlFor="name" className="block leading-6">
                      Campaign name (internal use) *
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="name"
                      placeholder="Campaign name "
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "name"
                      )}`}
                    />

                    {validationError("name") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("name")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="title" className="block leading-6">
                      Campaign title (shown in app) *
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="title"
                      placeholder="Campaign title *"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "title"
                      )}`}
                    />

                    {validationError("title") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("title")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="subtitle" className="block leading-6">
                      Campaign subtitle (shown in app)
                    </label>

                    <input
                      disabled={isSaving}
                      type="text"
                      name="subtitle"
                      placeholder="Campaign subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "subtitle"
                      )}`}
                    />

                    {validationError("subtitle") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("subtitle")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <TitlePicker
                      label="Game Title"
                      value={formData.titleId}
                      required={false}
                      disabled={isSaving}
                      placeholder="Type to search for a title"
                      error={validationError("titleId")}
                      onChange={(id) => {
                        console.log("TitlePicker onChange:", id);
                        setIsClean(false);
                        setFormData((prev) => ({ ...prev, titleId: id || "" }));
                      }}
                    />

                    {validationError("titleId") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("titleId")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="startTime" className="block leading-6">
                      Campaign start time *
                    </label>

                    <input
                      disabled={isSaving}
                      type="datetime-local"
                      name="startTime"
                      placeholder="Campaign start time"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "startTime"
                      )}`}
                    />

                    {validationError("startTime") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("startTime")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="endTime" className="block leading-6">
                      Campaign end time
                    </label>

                    <input
                      disabled={isSaving}
                      type="datetime-local"
                      name="endTime"
                      placeholder="Campaign end time"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "endTime"
                      )}`}
                    />

                    {validationError("endTime") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("endTime")}
                      </div>
                    )}
                  </div>
                  <div className="">
                    <label htmlFor="actionType" className="block leading-6">
                      Call to action (optional button shown in app)
                    </label>

                    <select
                      disabled={isSaving}
                      name="actionType"
                      value={formData.actionType}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                        "actionType"
                      )}`}
                    >
                      <option value="">No call to action</option>
                      <option value="web_url">Open web URL</option>
                      <option value="store_page">Open Meta store page</option>
                    </select>

                    {validationError("actionType") && (
                      <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                        {validationError("actionType")}
                      </div>
                    )}
                  </div>
                  {formData.actionType === "web_url" && (
                    <div className="">
                      <label
                        htmlFor="destinationUrl"
                        className="block leading-6"
                      >
                        Call to action URL *
                      </label>

                      <input
                        disabled={isSaving}
                        type="url"
                        name="destinationUrl"
                        placeholder="https://example.com"
                        value={formData.destinationUrl}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                          "destinationUrl"
                        )}`}
                      />

                      {validationError("destinationUrl") && (
                        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                          {validationError("destinationUrl")}
                        </div>
                      )}
                    </div>
                  )}
                  {formData.actionType === "store_page" && (
                    <div className="">
                      <label htmlFor="appStoreId" className="block leading-6">
                        Meta store ID *
                      </label>

                      <input
                        disabled={isSaving}
                        type="text"
                        name="appStoreId"
                        placeholder="10058771020910978"
                        value={formData.appStoreId}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                          "appStoreId"
                        )}`}
                      />

                      {validationError("appStoreId") && (
                        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                          {validationError("appStoreId")}
                        </div>
                      )}
                    </div>
                  )}
                  {formData.actionType && (
                    <div className="">
                      <label htmlFor="buttonText" className="block leading-6">
                        Button text *
                      </label>

                      <input
                        disabled={isSaving}
                        type="text"
                        name="buttonText"
                        placeholder="Visit store"
                        value={formData.buttonText}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded text-black ${validStyle(
                          "buttonText"
                        )}`}
                      />

                      {validationError("buttonText") && (
                        <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                          {validationError("buttonText")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="card p-8 flex flex-col gap-4">
                  {formData.campaignImageUrl && !previewBlob && (
                    <div className="flex flex-col gap-2">
                      <img
                        src={formData.campaignImageUrl}
                        alt="Campaign"
                        className="w-full h-auto rounded"
                      />
                    </div>
                  )}
                  {previewBlob && (
                    <div className="flex flex-col gap-2">
                      <img
                        src={URL.createObjectURL(previewBlob)}
                        alt="Campaign"
                        className="w-full h-auto rounded"
                      />
                    </div>
                  )}
                  <label htmlFor="image" className="block leading-6 pt-4">
                    Campaign image (JPG or JPEG) *
                  </label>

                  <input
                    disabled={isSaving}
                    accept="image/jpg,image/jpeg"
                    type="file"
                    name="image"
                    onChange={handleImageFileChange}
                    className={`w-full px-3 py-2 border rounded text-white ${validStyle(
                      "image"
                    )}`}
                  />

                  {validationError("image") && (
                    <div className="text-white bg-red-800 py-1 px-2 rounded-xl mt-2">
                      {validationError("image")}
                    </div>
                  )}
                </div>
              </div>
              <div className="sm:col-span-4 flex items-center gap-2 justify-end mt-4">
                {formData.id && (
                  <NegativeButton
                    type="button"
                    disabled={isSaving}
                    className={`text-red-500 py-2 px-4 rounded border-red-900 border hover:bg-red-900/25 `}
                    onClick={() => setDeleteAlertOpen(true)}
                  >
                    Delete campaign
                  </NegativeButton>
                )}
                <PositiveButton type="submit" disabled={isSaving || isClean}>
                  {isSaving
                    ? "Saving..."
                    : formData.id && isClean
                    ? "Saved"
                    : "Save changes"}
                </PositiveButton>
                {isApproved !== undefined && !isApproved &&
                <PositiveButton type="button" disabled={isSaving} onClick={() => setIsApproving(true)}>
                  Approve campaign
                </PositiveButton>
                }
              </div>
            </form>
          )}

          {formData.id && (
            <div className="card p-8 flex flex-col gap-4 mt-4">
              <CampaignRewardsList campaignId={formData.id} />
            </div>
          )}

          {formData.id && (
            <div className="card p-8 flex flex-col gap-4 mt-4">
              <CampaignPreviewUsersList campaignId={formData.id} />
            </div>
          )}
        </div>
      </div>
      <SimpleAlert
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        title={`Are you sure you want to delete this campaign?`}
        message="This action cannot be undone and it will destroy all associated progress and rewards!"
        confirmText={`Delete ${formData.title}`}
        onConfirm={deleteThisCampaign}
      />
      <SimpleAlert
        open={isApproving}
        setOpen={setIsApproving}
        title="Are you sure you want to approve this campaign?"
        message="This will make the campaign live and available to users in the app."
        confirmText={`Approve and go live`}
        onConfirm={approveThisCampaign}
        usePositiveButton={true}
      />
    </section>
  );
}
